import { Injectable, Injector } from '@angular/core';
import { BaseAuthenticationBloc } from '@kauth';
import { SubscriptionBloc } from '@kbloc';
import { DataStoreService } from '@kservice';
import { Status } from '@ktypes/models';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PremiumGateService {
  constructor(
    private _injector: Injector,
    private _subscriptionBloc: SubscriptionBloc,
    private _dataStoreService: DataStoreService
  ) {}

  private _lastStatusFetchAt = 0;
  private _lastPremiumUnlockAt: number | null = null;
  private readonly _postUpgradeHydrationWindowMs = 2 * 60 * 1000;

  markPremiumUnlockedNow(): void {
    this._lastPremiumUnlockAt = Date.now();
  }

  shouldHydrateCardsAfterUpgrade(): boolean {
    if (this._lastPremiumUnlockAt == null) {
      return false;
    }
    return Date.now() - this._lastPremiumUnlockAt <= this._postUpgradeHydrationWindowMs;
  }

  private _ensureStatusRequested() {
    // Ensure we have a user id; otherwise, wait until auth data is ready.
    if (!this._dataStoreService?.authData?.user?.id) {
      this._dataStoreService.authData$
        .pipe(
          filter((authData) => !!authData?.user?.id),
          take(1)
        )
        .subscribe(() => this._ensureStatusRequested());
      return;
    }
    const now = Date.now();
    // Throttle status fetches to avoid rapid loops that can flicker UI.
    if (now - this._lastStatusFetchAt < 5000) return;
    this._lastStatusFetchAt = now;
    this._subscriptionBloc.getSubscriptionStatus();
  }

  private _eligibleOfferingId(): string | null {
    const subStatus = this._subscriptionBloc.subscriptionStatus?.data;
    if (subStatus && !subStatus.canonicalHasAccess && subStatus.canonicalCanPurchase) {
      return subStatus.canonicalOfferingId ?? null;
    }
    return null;
  }

  private _getAuthBloc(): BaseAuthenticationBloc | null {
    try {
      return this._injector.get(BaseAuthenticationBloc);
    } catch {
      return null;
    }
  }

  /**
   * Best-effort premium gate:
   * - Uses cached subscription status (kicks off a refresh but does not await it)
   * - If eligible freemium, stores offeringId + returnUrl and signals the UI to show the paywall
   * - Otherwise returns false and caller can proceed (402 handler remains a safety net)
   */
  showPaywallIfEligible(returnUrl: string): boolean {
    this._ensureStatusRequested();
    const offeringId = this._eligibleOfferingId();
    if (offeringId) {
      this._getAuthBloc()?.setPremiumPaywallContext(offeringId, returnUrl);
      return true;
    }
    return false;
  }

  /**
   * Best-effort: returns the offering id if the user is eligible freemium.
   * Useful when you want to *render* a promo (not redirect).
   */
  getEligibleOfferingId(): string | null {
    this._ensureStatusRequested();
    return this._eligibleOfferingId();
  }

  /**
   * Best-effort async variant for views that render after subscription status resolves.
   * Returns once status is done/error so callers can reliably render premium placeholders.
   */
  getEligibleOfferingIdAsync(): Observable<string | null> {
    this._ensureStatusRequested();
    return this._subscriptionBloc.subscriptionStatus$.pipe(
      filter((status) => status?.status === Status.done || status?.status === Status.error),
      take(1),
      map(() => this._eligibleOfferingId())
    );
  }

  showPaywallIfEligibleAsync(returnUrl: string): Observable<boolean> {
    this._ensureStatusRequested();
    return this._subscriptionBloc.subscriptionStatus$.pipe(
      filter((status) => status?.status === Status.done || status?.status === Status.error),
      take(1),
      map((status) => {
        if (status?.status !== Status.done) {
          return false;
        }

        if (status?.data?.canonicalHasAccess === true) {
          return true;
        }

        this.showPaywallIfEligible(returnUrl);
        return false;
      })
    );
  }

  // Note: use showPaywallIfEligibleAsync when you need to await status.
}
